import { type ReactNode } from 'react';
import { SignedIn, SignedOut, SignIn, useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { getAccess } from '@/api/admin';
import { env } from '@/env';
import { ApiError, NetworkError } from '@/api/http';
import { Trans, useTranslation } from 'react-i18next';

/**
 * StaffGate — a consola é da EQUIPA Marcianus (eixo A). Só entra quem o servidor
 * confirma como staff (`/access/me` → isStaff). NÃO é o bundle que decide: a
 * API é a fronteira real (rejeita `platform.*` a quem não é staff). Este gate é
 * a cortesia de UX — mostrar "sem acesso" em vez de uma cascata de 403.
 *
 * Assim, mesmo que alguém abra o subdomínio admin, sem eixo A não vê nem faz nada.
 */
export function StaffGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('hq');
  // Harness de QA visual: em preview a consola abre sem gate, sobre dados de
  // amostra (src/mocks/preview.ts). É o mesmo compromisso que a app lean já
  // fazia — sem isto, NENHUMA página da consola pode ser auditada, fotografada
  // ou testada, porque todas exigem uma sessão Clerk real e um /access/me a
  // dizer isStaff. O ramo não existe em produção: `env.preview` vem de uma
  // variável que o build de produção não define.
  if (env.preview) return <>{children}</>;
  return (
    <>
      <SignedOut>
        <div className="center-screen">
          <div className="auth-card">
            <div className="brand-mark brand-mark-logo">
              <img src="/symbol.png" alt="Marcianus" width={34} height={34} />
            </div>
            <h1>Marcianus HQ</h1>
            <p className="muted">{t('gate.internal')}</p>
            <SignIn routing="hash" />
            <p className="auth-legal">{t('gate.restricted')}</p>
          </div>
        </div>
      </SignedOut>
      <SignedIn>
        <StaffOnly>{children}</StaffOnly>
      </SignedIn>
    </>
  );
}

function StaffOnly({ children }: { children: ReactNode }) {
  const { t } = useTranslation('hq');
  const { user } = useUser();
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['access'],
    queryFn: getAccess,
    retry: (count, err) => err instanceof NetworkError && count < 2,
  });
  const email = user?.primaryEmailAddress?.emailAddress;

  if (isLoading) return <div className="center-screen"><div className="spinner" /></div>;

  // ⚠ Ordem deliberada: "sem permissão" é a ÚLTIMA hipótese, não a primeira.
  // Antes era `if (isError || !data?.isStaff)` → QUALQUER falha acusava a conta
  // de não pertencer à equipa. A 2026-07-16 o CORS_ORIGIN da API não tinha o
  // domínio da consola: o /access/me era bloqueado e o ecrã dizia ao founder
  // que a conta dele não era da equipa. Mentira, e mandou-nos investigar
  // permissões durante um problema de infraestrutura.
  if (error) {
    if (error instanceof NetworkError) {
      return (
        <Problem
          icon="⚡"
          title={t('gate.noApiTitle')}
          lead={<Trans t={t} i18nKey="gate.noApiLead" components={{ b: <b /> }} />}
          hint={t('gate.noApiHint')}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      );
    }
    const status = error instanceof ApiError ? error.status : 0;
    if (status === 401) {
      return (
        <Problem icon="⏱" title={t('gate.expiredTitle')} lead={t('gate.expiredLead')}
          hint={t('gate.expiredHint')} onRetry={() => refetch()} retrying={isRefetching} />
      );
    }
    if (status >= 500) {
      return (
        <Problem icon="✕" title={t('gate.serverTitle')} lead={<Trans t={t} i18nKey="gate.serverLead" values={{ status }} components={{ b: <b /> }} />}
          hint={t('gate.serverHint')}
          onRetry={() => refetch()} retrying={isRefetching} />
      );
    }
    if (status !== 403) {
      return (
        <Problem icon="✕" title={t('gate.unexpectedTitle')} lead={<Trans t={t} i18nKey="gate.unexpectedLead" values={{ status }} components={{ b: <b /> }} />}
          hint={t('gate.unexpectedHint')} onRetry={() => refetch()} retrying={isRefetching} />
      );
    }
    // 403 cai para o caso de permissão abaixo — aí a acusação é verdadeira.
  }

  if (!data?.isStaff) {
    return (
      <Problem
        icon="✕"
        title={t('gate.deniedTitle')}
        lead={email
          ? <Trans t={t} i18nKey="gate.deniedLeadEmail" values={{ email }} components={{ b: <b /> }} />
          : t('gate.deniedLead')}
        hint={t('gate.deniedHint')}
      />
    );
  }
  return <>{children}</>;
}

/** Ecrã de problema. Um só componente → as 5 saídas não divergem no visual. */
function Problem({ icon, title, lead, hint, onRetry, retrying }: {
  icon: string; title: string; lead: ReactNode; hint: string;
  onRetry?: () => void; retrying?: boolean;
}) {
  const { t } = useTranslation('hq');
  return (
    <div className="center-screen">
      <div className="auth-card">
        <div className="brand-mark danger">{icon}</div>
        <h1>{title}</h1>
        <p className="muted">{lead}</p>
        <p className="muted small">{hint}</p>
        {onRetry && (
          <button className="btn btn-purple btn-block" onClick={onRetry} disabled={retrying}>
            {retrying ? t('gate.retrying') : t('gate.retry')}
          </button>
        )}
      </div>
    </div>
  );
}
